import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";

const API_BASE = "/api/obto-ops-control-room";

async function api(path, options = {}) {
  const response = await fetch(API_BASE + path, {
    ...options,
    headers: { "Content-Type": "application/json", ...(options.headers || {}) }
  });
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    throw new Error(options.method || "GET" + " " + path + " returned non-JSON response");
  }
  const data = await response.json();
  if (!response.ok || !data.ok) throw new Error(data.error || ("Request failed: " + response.status));
  return data;
}

const formatTime = value => value ? new Date(value).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—";
const label = value => String(value || "").replaceAll("_", " ");

function StatusPill({ value, tone }) {
  return <span className={"pill " + (tone || value || "")}>{label(value)}</span>;
}

function EmptyState({ onCreate, busy }) {
  return (
    <section className="empty card">
      <div className="empty-mark">OB</div>
      <p className="eyebrow">Synthetic proof environment</p>
      <h2>Start one governed workflow</h2>
      <p>Create a synthetic onboarding case, evaluate transparent rules, pause at a named approval boundary, and verify the persisted result.</p>
      <button className="primary" onClick={onCreate} disabled={busy}>{busy ? "Creating…" : "Create synthetic case"}</button>
      <small>No customer data, external messages, or production actions.</small>
    </section>
  );
}

function CasePanel({ item }) {
  const facts = item.facts || {};
  return (
    <section className="card panel">
      <div className="section-heading">
        <div><p className="eyebrow">01 · Case</p><h2>Bounded input</h2></div>
        <StatusPill value={item.status} />
      </div>
      <div className="case-title">
        <span className="case-icon">NS</span>
        <div><strong>{facts.organization}</strong><span>{item._id}</span></div>
      </div>
      <dl className="facts">
        <div><dt>Trigger</dt><dd>{label(item.trigger?.type)}</dd></div>
        <div><dt>Freshness</dt><dd>{formatTime(item.trigger?.receivedAt)}</dd></div>
        <div><dt>Scope</dt><dd>{(item.scope?.permitted || []).length} permitted actions</dd></div>
        <div><dt>Classification</dt><dd>{label(facts.dataClassification)}</dd></div>
      </dl>
      <div className="evidence-list">
        {[
          ["Identity evidence", facts.identityVerified],
          ["Agreement evidence", facts.agreementSigned],
          ["Billing evidence", facts.billingConfigured]
        ].map(([name, passed]) => (
          <div key={name}><span>{passed ? "✓" : "×"}</span><p>{name}<small>{passed ? "Present and persisted" : "Missing"}</small></p></div>
        ))}
      </div>
      <div className="scope-note"><b>Explicitly out of scope</b><p>{(item.scope?.prohibited || []).map(label).join(" · ")}</p></div>
    </section>
  );
}

function DecisionPanel({ item, onEvaluate, onDecide, busy }) {
  const decision = item.decision || {};
  const approval = item.approval || {};
  const pending = approval.status === "pending";
  return (
    <section className="card panel decision-panel">
      <div className="section-heading">
        <div><p className="eyebrow">02 · Decision</p><h2>Policy boundary</h2></div>
        <StatusPill value={decision.riskLevel} />
      </div>
      <div className="score">
        <div className="score-ring" style={{"--score": (decision.readinessScore || 0) + "%"}}><b>{decision.readinessScore || 0}</b><span>ready</span></div>
        <div><p className="eyebrow">Recommendation</p><strong>{decision.recommendation}</strong></div>
      </div>
      <div className="policy-meta">
        <div><span>Policy</span><b>{decision.policyVersion}</b></div>
        <div><span>Authority</span><b>{decision.authority?.name}</b><small>{decision.authority?.role}</small></div>
        <div><span>Expiry</span><b>{formatTime(decision.expiresAt)}</b></div>
      </div>
      <div className="rules">
        {(decision.rules || []).map(rule => (
          <div key={rule.id}><span className={rule.passed ? "pass" : "fail"}>{rule.passed ? "Pass" : "Fail"}</span><p>{rule.label}</p><b>{rule.weight}%</b></div>
        ))}
      </div>
      <div className="approval-box">
        <div className="approval-top">
          <div><p className="eyebrow">Approval request</p><h3>{label(approval.action)}</h3></div>
          <StatusPill value={approval.status} tone={approval.status} />
        </div>
        <p className="boundary">OBTO prepared the permitted next step. It did not execute the approval-bound action.</p>
        {pending ? (
          <div className="actions">
            <button className="primary" onClick={() => onDecide("approved")} disabled={busy}>Approve as Maya Chen</button>
            <button className="secondary" onClick={() => onDecide("rejected")} disabled={busy}>Reject</button>
            <button className="text-button" onClick={onEvaluate} disabled={busy}>Re-evaluate policy</button>
          </div>
        ) : (
          <div className="decision-receipt">
            <span>✓</span><p><b>{label(approval.status)} by {approval.decidedBy?.name}</b><small>{formatTime(approval.decidedAt)}</small></p>
          </div>
        )}
      </div>
    </section>
  );
}

function EvidencePanel({ item, audit, health }) {
  const verified = item.verification?.status === "verified";
  return (
    <section className="card panel evidence-panel">
      <div className="section-heading">
        <div><p className="eyebrow">03 · Evidence</p><h2>Ordered proof</h2></div>
        <StatusPill value={verified ? "verified" : "pending"} />
      </div>
      <div className="proof-rail">
        <div><span className="live-dot"></span><p>API route</p><b>{health?.ok ? "Live JSON" : "Checking"}</b></div>
        <div><span>DB</span><p>Persistence</p><b>{item._id ? "Stored" : "Pending"}</b></div>
        <div><span>v1</span><p>Policy</p><b>{item.decision?.policyVersion}</b></div>
        <div><span>{verified ? "✓" : "…"}</span><p>Outcome</p><b>{verified ? "Verified" : "Awaiting decision"}</b></div>
      </div>
      {verified && <div className="verified-banner"><span>✓</span><div><b>Persisted outcome verified</b><p>{item.verification.outcome}</p></div></div>}
      <div className="timeline">
        {(audit || item.events || []).map(event => (
          <article key={event.id}>
            <div className="event-seq">{String(event.sequence).padStart(2, "0")}</div>
            <div><header><b>{event.title}</b><time>{formatTime(event.at)}</time></header><p>{event.detail}</p><footer><span>{event.actor}</span><span>{event.policyVersion}</span></footer></div>
          </article>
        ))}
      </div>
    </section>
  );
}

function App() {
  const [cases, setCases] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [audit, setAudit] = useState([]);
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const selected = useMemo(() => cases.find(item => item._id === selectedId) || cases[0] || null, [cases, selectedId]);

  const refresh = async preferredId => {
    const [healthData, casesData] = await Promise.all([api("/health"), api("/cases")]);
    setHealth(healthData);
    setCases(casesData.cases);
    const id = preferredId || selectedId || casesData.cases[0]?._id;
    if (id) {
      setSelectedId(id);
      const auditData = await api("/audit/" + encodeURIComponent(id));
      setAudit(auditData.events);
    } else {
      setAudit([]);
    }
  };

  useEffect(() => {
    refresh().catch(err => setError(err.message));
  }, []);

  const run = async work => {
    setBusy(true);
    setError("");
    try { await work(); } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const createCase = () => run(async () => {
    const data = await api("/cases", { method: "POST", body: JSON.stringify({ synthetic: true }) });
    await refresh(data.case._id);
  });

  const evaluate = () => run(async () => {
    await api("/cases/" + encodeURIComponent(selected._id) + "/decision", { method: "POST", body: "{}" });
    await refresh(selected._id);
  });

  const decide = value => run(async () => {
    await api("/approvals/" + encodeURIComponent(selected.approval.id), {
      method: "POST",
      body: JSON.stringify({ decision: value, approver: "Maya Chen" })
    });
    await refresh(selected._id);
  });

  return (
    <main>
      <header className="topbar">
        <a className="brand" href="/" aria-label="OBTO Operations Control Room"><span>OBTO</span><div><b>Operations Control Room</b><small>From prompt to verified workflow</small></div></a>
        <div className="system-state"><span className="live-dot"></span><p>Governed MCP application</p><b>{health?.version || "1.0.0"}</b></div>
      </header>
      <section className="hero">
        <div><p className="eyebrow">BUILT · OPERATED · VERIFIED ON OBTO</p><h1>Every action has a boundary.<br/><em>Every change has proof.</em></h1></div>
        <p>This synthetic case shows OBTO persisting evidence, applying a versioned policy, stopping for named approval, and verifying the resulting state.</p>
      </section>
      {error && <div className="error-banner" role="alert"><b>Live workflow error</b><span>{error}</span><button onClick={() => refresh().catch(err => setError(err.message))}>Retry</button></div>}
      {!selected ? <EmptyState onCreate={createCase} busy={busy} /> : (
        <>
          <nav className="case-nav">
            <label>Persisted case<select value={selected._id} onChange={e => { setSelectedId(e.target.value); refresh(e.target.value).catch(err => setError(err.message)); }}>{cases.map(item => <option key={item._id} value={item._id}>{item.facts.organization} · {label(item.status)}</option>)}</select></label>
            <button className="secondary" onClick={createCase} disabled={busy}>+ New synthetic case</button>
          </nav>
          <div className="workspace">
            <CasePanel item={selected} />
            <DecisionPanel item={selected} onEvaluate={evaluate} onDecide={decide} busy={busy} />
            <EvidencePanel item={selected} audit={audit} health={health} />
          </div>
        </>
      )}
      <footer className="footer"><b>OBTO</b><p>Built through governed MCP tools. Operated within explicit scope. Verified against persisted state.</p><span>Synthetic demonstration · No external execution</span></footer>
    </main>
  );
}

const mount = () => {
  const el = document.getElementById("root");
  if (el) createRoot(el).render(<App />);
};
document.readyState === "loading"
  ? document.addEventListener("DOMContentLoaded", mount, { once: true })
  : mount();